import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ChartBarIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  DocumentArrowDownIcon,
  CakeIcon,
  ArrowPathIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '@/hooks/useAuth'
import {
  getMealStats,
  getMealTypes,
  getMealSessions,
  type MealStats,
  type MealType,
  type MealSession,
} from '@/api/mealsApi'
import ExportModal from '@/components/ui/ExportModal'
import { type ExportConfig } from '@/utils/exportUtils'
import toast from 'react-hot-toast'
import { format, subDays } from 'date-fns'

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']

// Interface for computed analytics from available endpoints
interface ComputedAnalytics {
  stats: MealStats | null
  sessions: MealSession[]
  sessionsByMealType: Array<{ name: string; count: number; totalActual: number }>
  dailyTrends: Array<{ date: string; sessions: number; actual: number; expected: number }>
  statusDistribution: Array<{ name: string; value: number }>
}

const MealReportsPage: React.FC = () => {
  const { user } = useAuth()
  const [analytics, setAnalytics] = useState<ComputedAnalytics | null>(null)
  const [mealTypes, setMealTypes] = useState<MealType[]>([])
  const [loading, setLoading] = useState(true)
  const [showExportModal, setShowExportModal] = useState(false)

  // Filters
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'custom'>('month')
  const [selectedMealType, setSelectedMealType] = useState<string>('')
  const [customDateRange, setCustomDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  })

  useEffect(() => {
    fetchMealTypes()
  }, [])

  useEffect(() => {
    fetchAnalytics()
  }, [selectedPeriod, selectedMealType, customDateRange])

  const getDateRange = () => {
    const endDate = new Date()
    let startDate: Date

    switch (selectedPeriod) {
      case 'week':
        startDate = subDays(endDate, 7)
        break
      case 'month':
        startDate = subDays(endDate, 30)
        break
      case 'quarter':
        startDate = subDays(endDate, 90)
        break
      case 'custom':
        return {
          startDate: customDateRange.startDate,
          endDate: customDateRange.endDate,
        }
      default:
        startDate = subDays(endDate, 30)
    }

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    }
  }

  const fetchMealTypes = async () => {
    try {
      const response = await getMealTypes()
      setMealTypes(response.data)
    } catch (error) {
      console.error('Failed to load meal types:', error)
    }
  }

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const { startDate, endDate } = getDateRange()

      // Fetch stats and sessions from available endpoints
      const [statsResponse, sessionsResponse] = await Promise.all([
        getMealStats({ startDate, endDate }),
        getMealSessions({
          startDate,
          endDate,
          mealTypeId: selectedMealType || undefined,
          limit: 100
        }),
      ])

      const stats = statsResponse.data
      const sessions = sessionsResponse.data?.sessions || []

      // Compute sessionsByMealType from sessions
      const mealTypeMap = new Map<string, { name: string; count: number; totalActual: number }>()
      sessions.forEach((session) => {
        const mealType = typeof session.mealTypeId === 'object' ? session.mealTypeId : null
        const mealTypeName = mealType?.name || 'Unknown'
        const mealTypeKey = mealType?._id || 'unknown'

        if (!mealTypeMap.has(mealTypeKey)) {
          mealTypeMap.set(mealTypeKey, { name: mealTypeName, count: 0, totalActual: 0 })
        }
        const entry = mealTypeMap.get(mealTypeKey)!
        entry.count += 1
        entry.totalActual += session.actualCount || 0
      })
      const sessionsByMealType = Array.from(mealTypeMap.values())

      // Compute daily trends
      const dailyMap = new Map<string, { sessions: number; actual: number; expected: number }>()
      sessions.forEach((session) => {
        const date = session.sessionDate.split('T')[0]
        if (!dailyMap.has(date)) {
          dailyMap.set(date, { sessions: 0, actual: 0, expected: 0 })
        }
        const entry = dailyMap.get(date)!
        entry.sessions += 1
        entry.actual += session.actualCount || 0
        entry.expected += session.expectedCount || 0
      })
      const dailyTrends = Array.from(dailyMap.entries())
        .map(([date, data]) => ({ date, ...data }))
        .sort((a, b) => a.date.localeCompare(b.date))

      // Compute status distribution
      const statusMap = new Map<string, number>()
      sessions.forEach((session) => {
        const status = session.status || 'unknown'
        statusMap.set(status, (statusMap.get(status) || 0) + 1)
      })
      const statusDistribution = Array.from(statusMap.entries()).map(([name, value]) => ({ name, value }))

      setAnalytics({
        stats,
        sessions,
        sessionsByMealType,
        dailyTrends,
        statusDistribution,
      })
    } catch (error: any) {
      console.error('Failed to load analytics:', error)
      toast.error(error.response?.data?.message || 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  const getExportConfig = (): ExportConfig => {
    const { startDate, endDate } = getDateRange()
    const totalSessions = analytics?.stats?.totalSessions || 0
    const completedSessions = analytics?.stats?.completedSessions || 0
    const totalExpected = analytics?.stats?.attendance?.totalExpected || 0
    const totalActual = analytics?.stats?.attendance?.totalActual || 0
    const attendanceRate = analytics?.stats?.attendance?.rate || '0%'

    return {
      title: 'Meal Reports',
      subtitle: 'Comprehensive meal attendance and session analytics',
      filename: `meal-reports-${startDate}-to-${endDate}`,
      dateRange: { from: startDate, to: endDate },
      generatedAt: format(new Date(), 'PPpp'),
      summary: [
        { label: 'Total Sessions', value: totalSessions.toString() },
        { label: 'Completed Sessions', value: completedSessions.toString() },
        { label: 'Total Expected', value: totalExpected.toString() },
        { label: 'Total Actual', value: totalActual.toString() },
        { label: 'Attendance Rate', value: attendanceRate },
        { label: 'Meal Types', value: (analytics?.stats?.totalMealTypes || 0).toString() },
      ],
      sections: [
        {
          title: 'Sessions by Meal Type',
          columns: [
            { header: 'Meal Type', key: 'mealType' },
            { header: 'Sessions', key: 'sessions' },
            { header: 'Meals Served', key: 'mealsServed' },
          ],
          data: analytics?.sessionsByMealType?.map((item) => ({
            mealType: item.name,
            sessions: item.count,
            mealsServed: item.totalActual,
          })) || [],
        },
        {
          title: 'Session Status Distribution',
          columns: [
            { header: 'Status', key: 'status' },
            { header: 'Count', key: 'count' },
          ],
          data: analytics?.statusDistribution?.map((item) => ({
            status: item.name.charAt(0).toUpperCase() + item.name.slice(1).replace('_', ' '),
            count: item.value,
          })) || [],
        },
        {
          title: 'Daily Trends',
          columns: [
            { header: 'Date', key: 'date' },
            { header: 'Sessions', key: 'sessions' },
            { header: 'Expected', key: 'expected' },
            { header: 'Actual', key: 'actual' },
          ],
          data: analytics?.dailyTrends?.slice(-14).map((item) => ({
            date: format(new Date(item.date), 'MMM d, yyyy'),
            sessions: item.sessions,
            expected: item.expected,
            actual: item.actual,
          })) || [],
        },
        {
          title: 'Recent Sessions',
          columns: [
            { header: 'Meal Type', key: 'mealType' },
            { header: 'Date', key: 'date' },
            { header: 'Time', key: 'time' },
            { header: 'Status', key: 'status' },
            { header: 'Expected', key: 'expected' },
            { header: 'Actual', key: 'actual' },
          ],
          data: analytics?.sessions?.slice(0, 20).map((session) => {
            const mealType = typeof session.mealTypeId === 'object' ? session.mealTypeId?.name : 'N/A'
            return {
              mealType: mealType,
              date: format(new Date(session.sessionDate), 'MMM d, yyyy'),
              time: `${session.startTime} - ${session.endTime}`,
              status: session.status.charAt(0).toUpperCase() + session.status.slice(1).replace('_', ' '),
              expected: session.expectedCount,
              actual: session.actualCount,
            }
          }) || [],
        },
      ],
    }
  }

  const formatTime = (time: string) => {
    if (!time) return 'N/A'
    const [hours, minutes] = time.split(':')
    const h = parseInt(hours)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayHour = h % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  const getAttendanceRate = (expected: number, actual: number) => {
    if (expected === 0) return '0%'
    return `${((actual / expected) * 100).toFixed(1)}%`
  }

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meal Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">
            Comprehensive insights into meal attendance and trends
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchAnalytics}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Period:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value as any)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last 90 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {selectedPeriod === 'custom' && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">From:</label>
                <input
                  type="date"
                  value={customDateRange.startDate}
                  onChange={(e) =>
                    setCustomDateRange({ ...customDateRange, startDate: e.target.value })
                  }
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">To:</label>
                <input
                  type="date"
                  value={customDateRange.endDate}
                  onChange={(e) =>
                    setCustomDateRange({ ...customDateRange, endDate: e.target.value })
                  }
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Meal Type:</label>
            <select
              value={selectedMealType}
              onChange={(e) => setSelectedMealType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Meal Types</option>
              {mealTypes.map((mt) => (
                <option key={mt._id} value={mt._id}>
                  {mt.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(analytics?.stats?.totalSessions || 0)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-600">
              {analytics?.stats?.completedSessions || 0} completed
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Meals Served</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(analytics?.stats?.attendance?.totalActual || 0)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CakeIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-600">
              of {formatNumber(analytics?.stats?.attendance?.totalExpected || 0)} expected
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Attendance Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics?.stats?.attendance?.rate || '0%'}
              </p>
            </div>
            <div className="p-3 bg-amber-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-amber-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-600">Average attendance</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Meal Types</p>
              <p className="text-2xl font-bold text-gray-900">
                {analytics?.stats?.totalMealTypes || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3 flex items-center text-sm">
            <span className="text-gray-600">
              {analytics?.sessionsByMealType?.length || 0} active
            </span>
          </div>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Attendance Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Attendance Trend</h3>
          {analytics?.dailyTrends && analytics.dailyTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={analytics.dailyTrends}>
                <defs>
                  <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorExpected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => format(new Date(value), 'MMM d')}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                  labelFormatter={(value) => format(new Date(value), 'MMM d, yyyy')}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="actual"
                  name="Actual"
                  stroke="#3B82F6"
                  fill="url(#colorActual)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="expected"
                  name="Expected"
                  stroke="#10B981"
                  fill="url(#colorExpected)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No trend data available
            </div>
          )}
        </motion.div>

        {/* Sessions by Meal Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sessions by Meal Type</h3>
          {analytics?.sessionsByMealType && analytics.sessionsByMealType.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.sessionsByMealType}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
                <Legend />
                <Bar dataKey="count" name="Sessions" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="totalActual" name="Meals Served" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No session data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Status Distribution</h3>
          {analytics?.statusDistribution && analytics.statusDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={analytics.statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {analytics.statusDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No status data available
            </div>
          )}
        </motion.div>

        {/* Top Meal Types */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Meal Types Performance</h3>
          {analytics?.sessionsByMealType && analytics.sessionsByMealType.length > 0 ? (
            <div className="space-y-4">
              {analytics.sessionsByMealType.slice(0, 5).map((mealType, index) => (
                <div
                  key={mealType.name}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-lg text-white font-bold ${
                      index === 0
                        ? 'bg-amber-500'
                        : index === 1
                        ? 'bg-gray-400'
                        : index === 2
                        ? 'bg-amber-700'
                        : 'bg-gray-300'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{mealType.name}</div>
                    <div className="text-sm text-gray-600">
                      {mealType.count} sessions
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-green-600">
                      {formatNumber(mealType.totalActual)}
                    </div>
                    <div className="text-xs text-gray-500">meals served</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500">
              No meal type data available
            </div>
          )}
        </motion.div>
      </div>

      {/* Recent Sessions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Sessions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Meal Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expected
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {analytics?.sessions && analytics.sessions.length > 0 ? (
                analytics.sessions.slice(0, 10).map((session) => {
                  const mealType = typeof session.mealTypeId === 'object' ? session.mealTypeId : null
                  const mealTypeName = mealType?.name || 'Unknown'
                  const rate = getAttendanceRate(session.expectedCount, session.actualCount)

                  return (
                    <tr key={session._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CakeIcon className="h-5 w-5 text-amber-500" />
                          <span className="font-medium text-gray-900">{mealTypeName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {format(new Date(session.sessionDate), 'MMM d, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatTime(session.startTime)} - {formatTime(session.endTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            session.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : session.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800'
                              : session.status === 'scheduled'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {session.status === 'in_progress' ? 'In Progress' : session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {session.expectedCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {session.actualCount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`text-sm font-medium ${
                            parseFloat(rate) >= 80
                              ? 'text-green-600'
                              : parseFloat(rate) >= 50
                              ? 'text-amber-600'
                              : 'text-red-600'
                          }`}
                        >
                          {rate}
                        </span>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No recent sessions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        config={getExportConfig()}
        title="Export Meal Reports"
      />
    </div>
  )
}

export default MealReportsPage
